import type {
  Portfolio,
  PortfolioInstructions,
  PortfolioWithInstructions,
} from "@/types";

/**
 * Type guard to check if a portfolio has instructions
 *
 * @param portfolio - Portfolio to check
 * @returns True if portfolio has instructions, false otherwise
 */
export function hasInstructions(
  portfolio: Portfolio
): portfolio is PortfolioWithInstructions {
  return portfolio.portfolio_instructions !== null;
}

/**
 * Create an empty portfolio instructions object with proper field defaults
 *
 * @param allocatePercentage - Default allocation percentage (0 for regular portfolios, 100 for PPA portfolios)
 * @returns New PortfolioInstructions with default values
 */
export function createEmptyPortfolioInstructions(
  allocatePercentage = 0
): PortfolioInstructions {
  return {
    allocate_percentage: allocatePercentage,
    limit_price: null,
    duration_from_issuance: null,
    registry_device_ids: [],
    limits: [],
  };
}

/**
 * Filter portfolios to only include those with instructions
 *
 * @param portfolios - Array of portfolios to filter
 * @returns Array of portfolios that have instructions
 */
export function filterPortfoliosWithInstructions(
  portfolios: Portfolio[]
): PortfolioWithInstructions[] {
  return portfolios.filter(hasInstructions);
}

export function getPortfolioWithGuaranteedInstructions(
  portfolio: Portfolio
): PortfolioWithInstructions {
  if (hasInstructions(portfolio)) {
    return portfolio;
  }
  return {
    ...portfolio,
    portfolio_instructions: createEmptyPortfolioInstructions(),
  };
}
